import { useMemo, useState } from "react";

import { CadencePlaybook } from "../../Pages/GuidancePlaybooks/CadenceCard";
import { useAppSelector } from "../../Store/Hooks";
import {
  CADENCE_GENERIC_LABEL,
  CADENCE_ORDER,
  CadenceKey,
  getTodayCadence,
  resolveCadenceLabel,
} from "../../Utils/Guidance/guidanceCadence";
import { resolveGuidanceLinks } from "../../Utils/Guidance/guidanceLinkResolver";
import { buildRhythmRoleView } from "../../Utils/Guidance/guidanceRhythm";
import {
  GuidanceRole,
  getDefaultGuidanceRole,
} from "../../Utils/Guidance/guidanceRole";
import { getAccessibleLinkKeysForRole } from "../../Utils/Guidance/guidanceRoleAccess";

const EMPTY_PLAYBOOK: CadencePlaybook = {
  heading: "",
  time: "",
  nav: "",
  what: [],
  lookfor: [],
  links: [],
};

/**
 * Shared "what does today call for" logic behind the home widget, the daily
 * modal, and the guide drawer — one place computing role + today's
 * cadence + resolved content so all three surfaces stay in sync.
 *
 * Playbook body, nav chips, and Rhythm chrome all come from the API only
 * (no static JSON fallback). Surfaces should wait on `isReady` / `isLoading`.
 */
export interface UseTodaysGuidanceOptions {
  dateOverride?: Date;
  /** Show a specific cadence's playbook instead of today's (drawer/float
   * "switch cadence" pills). */
  cadenceOverride?: CadenceKey | null;
  /** Seed the role (e.g. carrying over the Hub's currently-selected
   * role when opening the drawer from a Hub card) instead of defaulting
   * to the signed-in user's role mapping. */
  initialPersona?: GuidanceRole | null;
}

export const useTodaysGuidance = (
  userRole: string | { name?: string } | null | undefined,
  options: UseTodaysGuidanceOptions = {}
) => {
  const { dateOverride, cadenceOverride, initialPersona } = options;
  const [personaOverride, setPersonaOverride] = useState<GuidanceRole | null>(
    initialPersona || null
  );

  const {
    navLinks,
    navLinksLoading,
    playbookLinks,
    playbookLinksLoading,
    rhythmProfiles,
    rhythmCadences,
    rhythmLoading,
    cadenceSchedule,
    previewDayOverride,
    previewFirstWeekOverride,
    previewFirstQuarterOverride,
  } = useAppSelector(state => state.guidance);

  const defaultPersona = useMemo(
    () => getDefaultGuidanceRole(userRole),
    [userRole]
  );
  const role = personaOverride || defaultPersona;

  const todayCadenceKey = useMemo(
    () =>
      getTodayCadence(
        dateOverride,
        cadenceSchedule,
        previewDayOverride,
        previewFirstWeekOverride,
        previewFirstQuarterOverride
      ),
    [
      dateOverride,
      cadenceSchedule,
      previewDayOverride,
      previewFirstWeekOverride,
      previewFirstQuarterOverride,
    ]
  );
  const cadenceKey = cadenceOverride || todayCadenceKey;
  const isToday = cadenceKey === todayCadenceKey;

  const accessibleLinkKeys = useMemo(
    () => getAccessibleLinkKeysForRole(userRole),
    [userRole]
  );

  const personaContent = useMemo(
    () =>
      buildRhythmRoleView(role, rhythmProfiles, rhythmCadences, playbookLinks),
    [role, rhythmProfiles, rhythmCadences, playbookLinks]
  );

  const liveContent = playbookLinks.find(
    p => p.role === role && p.cadence === cadenceKey
  );

  const playbook: CadencePlaybook = liveContent
    ? {
        heading: liveContent.heading || "",
        time: liveContent.time || "",
        nav: liveContent.nav || "",
        what: liveContent.what || [],
        lookfor: liveContent.lookfor || [],
        links: liveContent.links || [],
        agenda: (liveContent.agenda as CadencePlaybook["agenda"]) ?? undefined,
      }
    : EMPTY_PLAYBOOK;

  const cadenceLabel = resolveCadenceLabel(
    liveContent?.cadence_tag,
    cadenceKey
  );

  const resolvedLinks = resolveGuidanceLinks(
    playbook.links,
    accessibleLinkKeys,
    navLinks
  );

  const otherCadences = (
    personaContent.cadences.length
      ? personaContent.cadences
      : CADENCE_ORDER.map(key => ({
          key,
          label: CADENCE_GENERIC_LABEL[key],
          time: "",
          bullets: [] as string[],
        }))
  )
    .filter(c => c.key !== cadenceKey)
    .map(c => ({
      ...c,
      label: CADENCE_GENERIC_LABEL[c.key as CadenceKey] || c.label,
    }));

  const isLoading = playbookLinksLoading || navLinksLoading || rhythmLoading;
  const isReady = Boolean(liveContent) && !playbookLinksLoading;

  return {
    role,
    setPersonaOverride,
    cadenceKey,
    todayCadenceKey,
    isToday,
    cadenceLabel,
    playbook,
    personaContent,
    resolvedLinks,
    otherCadences,
    isLoading,
    isReady,
    navLinks,
  };
};
